/**
 * StructureTree — árbol estructural izquierdo del Designer.
 *
 * Render recursivo de las secciones del template con indentación por
 * nivel y soporte de drag-and-drop (vía `@dnd-kit/sortable`). Cada item:
 *
 *   - Numeración automática (1 / 1.2 / 1.2.3...).
 *   - Title (i18n del locale activo) + indicators i18n complete/partial.
 *   - BlockTypeBadge.
 *   - Badges semánticos: `fijo` (contenedor estructural) y `⊕N` (repetible).
 *   - Drag handle visible on hover.
 *
 * Click en item → SELECT_SECTION en el orchestrator.
 *
 * **DnD (B.6.d.3.b' Commit B):**
 *   - Drag vertical: reordena al mismo nivel.
 *   - Drop sobre un contenedor: se mueve dentro como último hijo.
 *   - Drop sobre una hoja: se mueve como sibling después de ella.
 *   - Reglas: no se puede soltar un nodo dentro de sí mismo o de sus
 *     descendientes (validado por el reducer).
 *
 * Footer:
 *   - Botón "Añadir sección" (con dropdown de block types disponibles).
 *   - Leyenda compacta plegable.
 */

import { useMemo, useState, type CSSProperties } from 'react' // useMemo usado en Stats
import { useTranslation } from 'react-i18next'
import {
  ChevronRight,
  GripVertical,
  IndentIncrease,
  IndentDecrease,
  Plus,
} from 'lucide-react'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import type {
  ArtifactStructuralSection,
  BlockType,
} from '../../../api/templates'
import { AddSectionPickerDialog } from './AddSectionPickerDialog'
import { BlockTypeBadge } from './BlockTypeBadge'
import {
  countAllNodes,
  isContainerType,
  isI18nComplete,
  walkTree,
  type DesignerState,
  type DesignerAction,
} from './state'

interface StructureTreeProps {
  state: DesignerState
  dispatch: React.Dispatch<DesignerAction>
  readOnly: boolean
}

// ─── Estructura plana derivada del árbol — la que consume DnD ──────────────
interface FlatNode {
  section_id: string
  parentId: string | null
  depth: number
  number: string // "1.2.3"
  type: BlockType
  repeatable: boolean
  hasChildren: boolean
  isContainer: boolean
}

function flattenTree(
  sections: ArtifactStructuralSection[],
  parentId: string | null = null,
  depth = 0,
  parentNumber = '',
): FlatNode[] {
  const out: FlatNode[] = []
  sections.forEach((node, idx) => {
    const number = parentNumber ? `${parentNumber}.${idx + 1}` : `${idx + 1}`
    out.push({
      section_id: node.section_id,
      parentId,
      depth,
      number,
      type: node.type as BlockType,
      repeatable: Boolean(node.repeatable),
      hasChildren: Boolean(node.children && node.children.length > 0),
      isContainer: isContainerType(node.type),
    })
    if (node.children && node.children.length > 0) {
      out.push(...flattenTree(node.children, node.section_id, depth + 1, number))
    }
  })
  return out
}

export function StructureTree({
  state,
  dispatch,
  readOnly,
}: StructureTreeProps) {
  const { t } = useTranslation('templates')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const flat = useMemo(
    () => flattenTree(state.definition.sections),
    [state.definition.sections],
  )

  // Aplicar estado de colapsado: omitir descendientes de nodos colapsados.
  const visibleFlat = useMemo(() => {
    if (collapsed.size === 0) return flat
    const hidden = new Set<string>()
    for (const node of flat) {
      if (collapsed.has(node.section_id)) {
        // Ocultar descendientes (todos los nodes que vengan después con depth
        // mayor hasta que vuelva al mismo o menor).
        const startDepth = node.depth
        const startIdx = flat.indexOf(node)
        for (let i = startIdx + 1; i < flat.length; i += 1) {
          const n = flat[i]
          if (!n) break
          if (n.depth <= startDepth) break
          hidden.add(n.section_id)
        }
      }
    }
    return flat.filter((n) => !hidden.has(n.section_id))
  }, [flat, collapsed])

  // Cuenta total de nodos para el header (sí incluye los colapsados).
  const totalCount = flat.length

  function toggleCollapsed(sectionId: string) {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(sectionId)) next.delete(sectionId)
      else next.add(sectionId)
      return next
    })
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Pequeña distancia para distinguir click vs drag.
      activationConstraint: { distance: 6 },
    }),
  )

  /** Indent: mueve `sectionId` al final de los children del hermano
   * inmediato anterior. Solo válido si ese hermano es contenedor. */
  function indentNode(sectionId: string) {
    const node = findStructural(state.definition.sections, sectionId)
    if (!node) return
    const parentId = flat.find((n) => n.section_id === sectionId)?.parentId
    const siblings =
      parentId == null
        ? state.definition.sections
        : findStructural(state.definition.sections, parentId)?.children ?? []
    const idx = siblings.findIndex((s) => s.section_id === sectionId)
    if (idx <= 0) return
    const prev = siblings[idx - 1]
    if (!prev || !isContainerType(prev.type)) return
    dispatch({
      type: 'MOVE_NODE',
      sectionId,
      newParentId: prev.section_id,
      position: prev.children?.length ?? 0,
    })
  }

  /** Outdent: mueve `sectionId` al nivel del padre, justo después del padre. */
  function outdentNode(sectionId: string) {
    const flatNode = flat.find((n) => n.section_id === sectionId)
    if (!flatNode || flatNode.parentId === null) return
    const parentFlat = flat.find((n) => n.section_id === flatNode.parentId)
    if (!parentFlat) return
    const grandParentId = parentFlat.parentId
    const grandSiblings =
      grandParentId == null
        ? state.definition.sections
        : findStructural(state.definition.sections, grandParentId)?.children ?? []
    const parentIdx = grandSiblings.findIndex(
      (s) => s.section_id === parentFlat.section_id,
    )
    if (parentIdx < 0) return
    dispatch({
      type: 'MOVE_NODE',
      sectionId,
      newParentId: grandParentId,
      position: parentIdx + 1,
    })
  }

  /** Capabilities por nodo — para enable/disable de los botones. */
  function capsFor(node: FlatNode): { canIndent: boolean; canOutdent: boolean } {
    const siblings =
      node.parentId == null
        ? state.definition.sections
        : findStructural(state.definition.sections, node.parentId)?.children ?? []
    const idx = siblings.findIndex((s) => s.section_id === node.section_id)
    const prev = idx > 0 ? siblings[idx - 1] : null
    return {
      canIndent: Boolean(prev && isContainerType(prev.type)),
      canOutdent: node.parentId !== null,
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const activeNode = flat.find((n) => n.section_id === active.id)
    const overNode = flat.find((n) => n.section_id === over.id)
    if (!activeNode || !overNode) return

    // Skip ciclos: si over está dentro del subárbol de active, no se puede.
    // (También lo enforce el reducer; aquí evitamos un dispatch que sería
    //  no-op y feedback raro.)
    if (isInSubtree(state.definition.sections, activeNode.section_id, overNode.section_id)) {
      return
    }

    // Regla universal de drop (sin auto-nesting):
    //  - El nodo activo toma el slot del nodo `over` en el padre de `over`.
    //  - Esto reproduce la semántica de `arrayMove` (anim de @dnd-kit) y
    //    matchea con la visualización durante el drag.
    //  - Para anidar dentro de un contenedor, el user usa el botón
    //    "+ Hijo" del editor (Commit C) o suelta sobre un nodo que YA
    //    es hijo del contenedor objetivo.
    const newParentId = overNode.parentId
    const siblings =
      newParentId === null
        ? state.definition.sections
        : findStructural(state.definition.sections, newParentId)?.children ?? []
    const overSiblingIdx = siblings.findIndex(
      (s) => s.section_id === overNode.section_id,
    )
    if (overSiblingIdx < 0) return

    dispatch({
      type: 'MOVE_NODE',
      sectionId: String(active.id),
      newParentId,
      position: overSiblingIdx,
    })
  }

  return (
    <aside className="flex flex-col h-full bg-surface border-r border-line min-h-0">
      <header className="px-4 py-3 border-b border-line flex items-baseline justify-between">
        <h2 className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
          {t('designer.structure.title')}
        </h2>
        <span className="text-[10px] text-ink-subtle">
          {t('designer.structure.node_count', { count: totalCount })}
        </span>
      </header>

      <div
        className="flex-1 overflow-y-auto p-2 space-y-0.5"
        onKeyDown={(e) => {
          if (readOnly || !state.selectedSectionId) return
          if (e.key === 'Tab') {
            e.preventDefault()
            if (e.shiftKey) outdentNode(state.selectedSectionId)
            else indentNode(state.selectedSectionId)
          }
        }}
        tabIndex={-1}
      >
        {totalCount === 0 ? (
          <p className="px-3 py-6 font-body text-xs text-ink-subtle text-center">
            {t('designer.structure.empty_hint')}
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={visibleFlat.map((n) => n.section_id)}
              strategy={verticalListSortingStrategy}
            >
              {visibleFlat.map((node) => {
                const i18nSection = state.definition.i18n[
                  state.activeLocale
                ]?.sections.find((s) => s.section_id === node.section_id)
                const caps = capsFor(node)
                return (
                  <SortableTreeItem
                    key={node.section_id}
                    node={node}
                    title={i18nSection?.title ?? ''}
                    selected={state.selectedSectionId === node.section_id}
                    isI18nComplete={isI18nComplete(
                      state.definition,
                      node.section_id,
                    )}
                    isCollapsed={collapsed.has(node.section_id)}
                    readOnly={readOnly}
                    canIndent={caps.canIndent}
                    canOutdent={caps.canOutdent}
                    onSelect={() =>
                      dispatch({
                        type: 'SELECT_SECTION',
                        sectionId: node.section_id,
                      })
                    }
                    onToggleCollapsed={() =>
                      toggleCollapsed(node.section_id)
                    }
                    onIndent={() => indentNode(node.section_id)}
                    onOutdent={() => outdentNode(node.section_id)}
                  />
                )
              })}
            </SortableContext>
          </DndContext>
        )}
      </div>

      {!readOnly ? (
        <div className="border-t border-line p-2">
          <AddSectionButton
            templateName={
              state.definition.i18n[state.activeLocale]?.name ?? ''
            }
            onAdd={(bt) => dispatch({ type: 'ADD_SECTION', blockType: bt })}
          />
        </div>
      ) : null}

      <Legend />
      <Stats state={state} />
    </aside>
  )
}

// ─── Item del árbol (sortable) ────────────────────────────────────────────
function SortableTreeItem({
  node,
  title,
  selected,
  isI18nComplete: i18nComplete,
  isCollapsed,
  readOnly,
  canIndent,
  canOutdent,
  onSelect,
  onToggleCollapsed,
  onIndent,
  onOutdent,
}: {
  node: FlatNode
  title: string
  selected: boolean
  isI18nComplete: boolean
  isCollapsed: boolean
  readOnly: boolean
  canIndent: boolean
  canOutdent: boolean
  onSelect: () => void
  onToggleCollapsed: () => void
  onIndent: () => void
  onOutdent: () => void
}) {
  const { t } = useTranslation('templates')
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: node.section_id, disabled: readOnly })

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    paddingLeft: `${node.depth * 16 + 8}px`,
    opacity: isDragging ? 0.4 : 1,
  }

  const containerClass = selected
    ? 'group rounded-md border border-brand bg-brand-soft/40 py-1.5 cursor-pointer'
    : 'group rounded-md border border-transparent hover:bg-surface-muted py-1.5 cursor-pointer'

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={containerClass}
      onClick={onSelect}
      role="button"
      aria-pressed={selected}
    >
      <div className="flex items-center gap-1.5 min-w-0 pr-2">
        {/* Drag handle */}
        {!readOnly ? (
          <button
            type="button"
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            aria-label={t('designer.structure.drag_handle')}
            className="p-0.5 rounded text-ink-subtle hover:text-ink hover:bg-surface-muted cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
          >
            <GripVertical className="w-3 h-3" />
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        {/* Toggle expand/collapse */}
        {node.hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onToggleCollapsed()
            }}
            aria-label={
              isCollapsed
                ? t('designer.structure.expand')
                : t('designer.structure.collapse')
            }
            className="p-0.5 rounded text-ink-muted hover:text-ink hover:bg-surface-muted shrink-0"
          >
            <ChevronRight
              className={`w-3 h-3 transition-transform ${
                isCollapsed ? '' : 'rotate-90'
              }`}
            />
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        {/* Number */}
        <span className="font-mono text-[10px] text-ink-subtle tabular-nums shrink-0">
          {node.number}
        </span>
        {/* Title */}
        <span
          className={
            title
              ? 'flex-1 truncate text-sm text-ink'
              : 'flex-1 truncate text-sm text-ink-subtle italic'
          }
          title={node.section_id}
        >
          {title || node.section_id}
        </span>
        {/* Indent / outdent (visible on hover, disabled cuando no aplican) */}
        {!readOnly ? (
          <span className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onOutdent()
              }}
              disabled={!canOutdent}
              aria-label={t('designer.structure.outdent')}
              title={t('designer.structure.outdent')}
              className="p-1 rounded hover:bg-surface-muted text-ink-muted hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <IndentDecrease className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onIndent()
              }}
              disabled={!canIndent}
              aria-label={t('designer.structure.indent')}
              title={t('designer.structure.indent')}
              className="p-1 rounded hover:bg-surface-muted text-ink-muted hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <IndentIncrease className="w-3 h-3" />
            </button>
          </span>
        ) : null}
      </div>
      <div className="mt-0.5 flex items-center gap-1.5 ml-12 text-[10px]">
        <BlockTypeBadge type={node.type} />
        {node.isContainer ? (
          <span
            className="font-mono text-[9px] uppercase tracking-wider px-1 rounded bg-official-soft text-official"
            title={t('designer.structure.legend.container')}
          >
            {t('designer.structure.badge_fixed')}
          </span>
        ) : null}
        {node.repeatable ? (
          <span
            className="font-mono text-[9px] uppercase tracking-wider px-1 rounded bg-brand-soft text-brand"
            title={t('designer.structure.legend.repeatable')}
          >
            {t('designer.structure.badge_repeatable')}
          </span>
        ) : null}
        <span
          className={
            i18nComplete
              ? 'ml-auto font-mono text-official'
              : 'ml-auto font-mono text-review'
          }
          title={
            i18nComplete
              ? t('designer.structure.i18n_complete')
              : t('designer.structure.i18n_partial')
          }
        >
          {i18nComplete ? '✓✓' : '⚠'}
        </span>
      </div>
    </div>
  )
}

// ─── Botón add — abre el modal picker ──────────────────────────────────────
function AddSectionButton({
  templateName,
  onAdd,
}: {
  templateName: string
  onAdd: (bt: BlockType) => void
}) {
  const { t } = useTranslation('templates')
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-dashed border-line py-2 text-xs text-ink-muted hover:text-ink hover:border-line-strong transition-colors"
      >
        <Plus className="w-3.5 h-3.5" />
        {t('designer.structure.add_section')}
      </button>
      <AddSectionPickerDialog
        open={open}
        templateName={templateName}
        onClose={() => setOpen(false)}
        onPick={(bt) => onAdd(bt)}
      />
    </>
  )
}

// ─── Leyenda ───────────────────────────────────────────────────────────────
function Legend() {
  const { t } = useTranslation('templates')
  return (
    <details className="border-t border-line text-[10px] text-ink-subtle">
      <summary className="px-3 py-2 cursor-pointer hover:text-ink-muted font-medium uppercase tracking-wider">
        {t('designer.structure.legend.title')}
      </summary>
      <ul className="px-3 pb-3 space-y-1.5 font-mono">
        <li className="flex items-baseline gap-2">
          <span className="bg-official-soft text-official px-1 rounded shrink-0">
            {t('designer.structure.badge_fixed')}
          </span>
          <span>{t('designer.structure.legend.container')}</span>
        </li>
        <li className="flex items-baseline gap-2">
          <span className="bg-brand-soft text-brand px-1 rounded shrink-0">
            {t('designer.structure.badge_repeatable')}
          </span>
          <span>{t('designer.structure.legend.repeatable')}</span>
        </li>
        <li className="flex items-baseline gap-2">
          <span className="text-official shrink-0">✓✓</span>
          <span>{t('designer.structure.legend.i18n_ok')}</span>
        </li>
        <li className="flex items-baseline gap-2">
          <span className="text-review shrink-0">⚠</span>
          <span>{t('designer.structure.legend.i18n_missing')}</span>
        </li>
        <li className="flex items-baseline gap-2">
          <span className="text-ink-subtle tabular-nums shrink-0">1.2.3</span>
          <span>{t('designer.structure.legend.numbering')}</span>
        </li>
        <li className="pt-1.5 border-t border-line">
          <span className="text-ink-muted">
            {t('designer.structure.legend.block_type_colors_intro')}
          </span>
        </li>
        <li className="flex items-baseline gap-2">
          <span className="bg-surface-muted text-ink-muted px-1 rounded shrink-0">
            txt
          </span>
          <span>{t('designer.structure.legend.color_text')}</span>
        </li>
        <li className="flex items-baseline gap-2">
          <span className="bg-review-soft text-review px-1 rounded shrink-0">
            list
          </span>
          <span>{t('designer.structure.legend.color_list')}</span>
        </li>
        <li className="flex items-baseline gap-2">
          <span className="bg-official-soft text-official px-1 rounded shrink-0">
            tbl
          </span>
          <span>{t('designer.structure.legend.color_table_diagram')}</span>
        </li>
        <li className="flex items-baseline gap-2">
          <span className="bg-brand-soft text-brand px-1 rounded shrink-0">
            kv
          </span>
          <span>{t('designer.structure.legend.color_structured')}</span>
        </li>
      </ul>
    </details>
  )
}

// ─── Stats (colapsable, junto a Legend) ────────────────────────────────────
function Stats({ state }: { state: DesignerState }) {
  const { t } = useTranslation('templates')
  const stats = useMemo(() => {
    let repeatable = 0
    const blockTypes = new Set<string>()
    walkTree(state.definition.sections, (n) => {
      if (n.repeatable) repeatable += 1
      blockTypes.add(n.type)
    })
    return {
      total: countAllNodes(state.definition),
      repeatable,
      locales: Object.keys(state.definition.i18n).length,
      blockTypes: blockTypes.size,
    }
  }, [state.definition])

  return (
    <details className="border-t border-line text-[10px] text-ink-subtle">
      <summary className="px-3 py-2 cursor-pointer hover:text-ink-muted font-medium uppercase tracking-wider">
        {t('designer.structure.stats_title')}
      </summary>
      <dl className="px-3 pb-3 space-y-1.5">
        <StatRow
          label={t('designer.preview.stats_sections')}
          value={stats.total}
        />
        <StatRow
          label={t('designer.preview.stats_repeatable')}
          value={stats.repeatable}
        />
        <StatRow
          label={t('designer.preview.stats_locales')}
          value={stats.locales}
        />
        <StatRow
          label={t('designer.preview.stats_block_types')}
          value={stats.blockTypes}
        />
      </dl>
    </details>
  )
}

function StatRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between text-[11px]">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="font-mono text-ink tabular-nums">{value}</dd>
    </div>
  )
}

// ─── Helpers locales ───────────────────────────────────────────────────────
function findStructural(
  sections: ArtifactStructuralSection[],
  sectionId: string,
): ArtifactStructuralSection | null {
  // Lookup recursivo simple.
  let found: ArtifactStructuralSection | null = null
  walkTree(sections, (n) => {
    if (n.section_id === sectionId && !found) found = n
  })
  return found
}

/** True si `targetId` vive dentro del subárbol enraizado en `rootId`
 * (incluye al propio root). Usado para evitar drops cíclicos. */
function isInSubtree(
  sections: ArtifactStructuralSection[],
  rootId: string,
  targetId: string,
): boolean {
  const root = findStructural(sections, rootId)
  if (!root) return false
  if (root.section_id === targetId) return true
  let found = false
  walkTree(root.children ?? [], (n) => {
    if (n.section_id === targetId) found = true
  })
  return found
}
